import React from 'react';
import PropTypes from 'prop-types';

/**
 * 로딩 스피너 컴포넌트
 */
export const LoadingSpinner = ({ 
  message = '로딩 중...', 
  size = 'default', // 'sm', 'default', 'lg'
  className = '',
  fullScreen = false
}) => {
  const sizeClasses = {
    sm: {
      spinner: 'h-4 w-4',
      text: 'text-xs'
    },
    default: {
      spinner: 'h-6 w-6',
      text: 'text-sm'
    },
    lg: {
      spinner: 'h-8 w-8',
      text: 'text-base'
    }
  };

  const classes = sizeClasses[size];
  
  const content = (
    <div className="flex flex-col items-center justify-center gap-3">
      <div className={`animate-spin rounded-full border-b-2 border-primary ${classes.spinner}`}></div>
      {message && (
        <p className={`text-gray-500 ${classes.text}`}>{message}</p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75 ${className}`}>
        {content}
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center py-8 ${className}`}>
      {content}
    </div>
  );
};

/**
 * 에러 메시지 컴포넌트
 */
export const ErrorMessage = ({ 
  error, 
  title = '오류가 발생했습니다',
  onRetry,
  className = ''
}) => {
  return (
    <div className={`p-6 text-center ${className}`}>
      <div className="mx-auto max-w-md">
        <div className="mb-4">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
            <svg className="size-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        
        <h3 className="mb-2 text-lg font-medium text-gray-900">{title}</h3>
        
        {error && (
          <p className="mb-4 text-sm text-gray-600">
            {typeof error === 'string' ? error : error.message || '알 수 없는 오류가 발생했습니다.'}
          </p>
        )}
        
        {onRetry && (
          <button
            onClick={onRetry}
            className="rounded bg-primary px-4 py-2 text-white transition-colors hover:bg-primary-hover"
          >
            다시 시도
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * 빈 상태 컴포넌트
 */
export const EmptyState = ({ 
  message = '데이터가 없습니다',
  description,
  icon,
  action,
  className = ''
}) => {
  return (
    <div className={`py-12 text-center ${className}`}>
      <div className="mx-auto max-w-md">
        {/* 아이콘 */}
        <div className="mb-4">
          {icon || (
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-gray-100">
              <svg className="size-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          )}
        </div>
        
        {/* 메시지 */}
        <h3 className="mb-2 text-lg font-medium text-gray-900">{message}</h3>
        
        {/* 설명 */}
        {description && (
          <p className="mb-6 text-sm text-gray-600">{description}</p>
        )}
        
        {/* 액션 */}
        {action && <div>{action}</div>}
      </div>
    </div>
  );
};

/**
 * 상태별 컨테이너 컴포넌트
 * 로딩, 에러, 빈 상태를 자동으로 처리
 */
export const StateContainer = ({ 
  loading = false,
  error = null,
  empty = false,
  children,
  loadingMessage,
  errorTitle,
  emptyMessage,
  emptyDescription,
  emptyAction,
  onRetry,
  className = ''
}) => {
  if (loading) {
    return <LoadingSpinner message={loadingMessage} className={className} />;
  }

  if (error) {
    return (
      <ErrorMessage 
        error={error} 
        title={errorTitle}
        onRetry={onRetry}
        className={className}
      />
    );
  }

  if (empty) {
    return (
      <EmptyState 
        message={emptyMessage}
        description={emptyDescription}
        action={emptyAction}
        className={className}
      />
    );
  }

  return children;
};

LoadingSpinner.propTypes = {
  message: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'default', 'lg']),
  className: PropTypes.string,
  fullScreen: PropTypes.bool
};

ErrorMessage.propTypes = {
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  title: PropTypes.string,
  onRetry: PropTypes.func,
  className: PropTypes.string
};

EmptyState.propTypes = {
  message: PropTypes.string,
  description: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.node,
  className: PropTypes.string
};

StateContainer.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  empty: PropTypes.bool,
  children: PropTypes.node,
  loadingMessage: PropTypes.string,
  errorTitle: PropTypes.string,
  emptyMessage: PropTypes.string,
  emptyDescription: PropTypes.string,
  emptyAction: PropTypes.node,
  onRetry: PropTypes.func,
  className: PropTypes.string
}; 